import type { InteractionBlockStore } from "../../interaction-block/InteractionBlockStore";
import type { AutoSpinStore } from "../../autospin/AutoSpinStore";
import { SpinCoordinator } from "../../spin/SpinCoordinator";
import type { SpeedModePopupPort, SpeedModeTogglePort } from "./SpeedModePorts";
import type { ViewportStore } from "@slot/presentation";

export interface SpeedModePresenterDeps {
  interactionBlockStore: InteractionBlockStore;
  autoSpinStore: AutoSpinStore;
  /** Operator settings: which speed modes the player may pick. */
  fastSpinEnabled: boolean;
  superFastSpinEnabled: boolean;
  viewport: ViewportStore;
}

/**
 * The speed picker (Fast / Super Fast): the bar toggle opens it (a popover on desktop, a blocking
 * overlay on mobile), the two rows are mutually exclusive — tapping Fast while Super Fast is on
 * drops to Fast, tapping the active row drops to normal — and the pick goes to the coordinator.
 * Operator gates hide a row, and a banned mode found at start falls back to normal — a guard: nothing saves
 * the pick, so the coordinator starts every boot at normal.
 */
export class SpeedModePresenter {
  private unsubscribe: Array<() => void> = [];
  private popupOpen = false;

  public constructor(
    private readonly deps: SpeedModePresenterDeps,
    private readonly spinCoordinator: SpinCoordinator,
    private readonly input: { toggle: SpeedModeTogglePort; popup: SpeedModePopupPort; onBeforeOpen?: () => void },
  ) {
    this.input.popup.bind({
      onClose: () => this.closePopup(),
      onTurboTap: () => this.handleTurboTap(),
      onSuperTurboTap: () => this.handleSuperTurboTap(),
      onOutsideClick: () => this.closePopup(),
    });
    this.input.toggle.setOnTap(() => this.handleTogglePopup());
  }

  public start(): void {
    const fastAllowed = this.deps.fastSpinEnabled;
    const superFastAllowed = this.deps.superFastSpinEnabled;
    this.input.popup.setTurboVisible(fastAllowed);
    this.input.popup.setSuperTurboVisible(superFastAllowed);
    // Both accelerated presets disabled: the picker has nothing to offer, the entry point goes.
    if (!fastAllowed && !superFastAllowed) this.input.toggle.setShown(false);
    const currentMode = this.spinCoordinator.getSnapshot().speedMode;
    if ((currentMode === "turbo" && !fastAllowed) || (currentMode === "super_turbo" && !superFastAllowed)) this.spinCoordinator.setSpeedMode("normal");
    this.unsubscribe = [
      // A shape change re-applies the blocking rule while open.
      this.deps.viewport.subscribe(() => {
        if (this.popupOpen) this.applyInteractionBlock();
        this.render();
      }),
      this.spinCoordinator.subscribe(() => this.handleSpinCoordinatorChange()),
    ];
    this.render();
  }

  public stop(): void {
    for (const off of this.unsubscribe) off();
    this.unsubscribe = [];
    this.input.popup.hide();
    this.popupOpen = false;
    this.deps.interactionBlockStore.remove("speed_mode_popup");
  }

  public render(): void {
    const speedMode = this.spinCoordinator.getSnapshot().speedMode;
    this.input.toggle.setMode(speedMode);
    this.input.popup.setTurboEnabled(speedMode === "turbo");
    this.input.popup.setSuperTurboEnabled(speedMode === "super_turbo");
    if (this.popupOpen) this.input.popup.show();
    else this.input.popup.hide();
  }

  public closeIfOpen(): void {
    if (this.popupOpen) this.closePopup();
  }

  private isDesktop(): boolean {
    return this.deps.viewport.getSnapshot().shape === "desktop";
  }

  private handleTogglePopup(): void {
    if (this.popupOpen) {
      this.closePopup();
      return;
    }
    this.input.onBeforeOpen?.();
    this.popupOpen = true;
    this.applyInteractionBlock();
    this.input.popup.show();
  }

  private closePopup(): void {
    this.popupOpen = false;
    this.input.popup.hide();
    this.deps.interactionBlockStore.remove("speed_mode_popup");
  }

  /** The mobile overlay blocks the game; the desktop popover leaves it live. */
  private applyInteractionBlock(): void {
    if (this.isDesktop()) this.deps.interactionBlockStore.remove("speed_mode_popup");
    else this.deps.interactionBlockStore.add("speed_mode_popup");
  }

  private handleTurboTap(): void {
    const current = this.spinCoordinator.getSnapshot().speedMode;
    this.spinCoordinator.setSpeedMode(current === "super_turbo" ? "turbo" : current === "turbo" ? "normal" : "turbo");
  }

  private handleSuperTurboTap(): void {
    const current = this.spinCoordinator.getSnapshot().speedMode;
    this.spinCoordinator.setSpeedMode(current === "super_turbo" ? "normal" : "super_turbo");
  }

  private handleSpinCoordinatorChange(): void {
    // The desktop popover closes when a manual spin starts (idle → requesting); the mobile overlay
    // stays open for mid-round speed changes, and an autospin run keeps the popover open too.
    const spinSnapshot = this.spinCoordinator.getSnapshot();
    const autoSpinRunning = this.deps.autoSpinStore.getSnapshot().running;
    if (this.popupOpen && this.isDesktop() && spinSnapshot.phase === "requesting" && !autoSpinRunning) this.closePopup();
    this.render();
  }
}
